/**
 * Material Request Service
 *
 * Handles material requests from technicians in the field.
 * Supports FIFO inventory consumption upon approval.
 *
 * DB table: material_requests (migration 027)
 * Columns: id, project_id, task_id, requested_by, inventory_id, batch_id,
 *          is_external, external_supplier, external_description,
 *          quantity_requested, unit, status (pending/approved/rejected/consumed/cancelled),
 *          reviewed_by, reviewed_at, rejection_reason, stock_movement_id,
 *          notes, photo_url, created_at, updated_at
 *
 * @module services/materialRequestService
 */

import { supabase } from './supabase';
import {
  MATERIAL_REQUEST_STATUS,
  PAGINATION,
} from '../utils/constants';
// #384 — reparto por cantidad (retiro parcial + compra parcial). Función pura,
// con gemelo en `mcp-server/src/utils/materialSplit.js` (carril MCP) y test de
// paridad que corre las dos contra la misma tabla de casos.
import {
  planMaterialSplit,
  round2,
  formatQty,
  stampSplitNote,
} from '../utils/materialSplit';
// #369 — mismo patrón de gemelo: `mcp-server/src/utils/rawTextFidelity.js`.
import { findUnquotableItems } from '../utils/rawTextFidelity';
// #514 — mismo patrón de gemelo: `mcp-server/src/utils/auditRef.js`. Escribe
// referencias LEGIBLES en `notes` en vez de uuids crudos.
import { materialConsumptionNote, draftPurchaseNote } from '../utils/auditRef';
// #604 — mismo patrón de gemelo: `mcp-server/src/utils/unitProvenance.js`.
// Decide si la unidad de un ítem la dijo un humano o la puso el modelo.
import {
  UNIT_SOURCE,
  classifyUnitProvenance,
  unitFromCatalogNote,
  INVENTED_UNIT_NOTE,
} from '../utils/unitProvenance';

/**
 * Check if a Supabase error is a "table not found" error (PGRST204/205, 42P01, 404).
 * Returns true when the material_requests table doesn't exist yet.
 */
const isTableNotFoundError = (error) =>
  error?.code === 'PGRST204' ||
  error?.code === 'PGRST205' ||
  error?.code === '42P01' ||
  error?.status === 404 ||
  error?.message?.includes('relation') ||
  error?.message?.includes('does not exist');

// ---------------------------------------------------------------------------
// #356 — ambiguous units. WEB LANE half of the fix.
//
// These two constants are a deliberate, documented DUPLICATE of
// `mcp-server/src/tools/smartMaterialRequestTools.js` (UNIT_UNSPECIFIED /
// AMBIGUOUS_UNIT_NOTE). The frontend bundle cannot import from the mcp-server
// package, and the same duplication pattern is already used by
// reconcileMaterialsTools for rollupStockLocations. The strings MUST stay
// byte-identical: the buyer's list and the correction tool both match on them,
// and a MR created from the web UI has to be indistinguishable from one created
// by the agent. If you change one, change the other.
//
// Why a sentinel and not NULL: `material_requests.unit` and
// `material_request_items.unit` are both `text NOT NULL` in prod (verified by
// SELECT on information_schema.columns), so NULL is not available without a
// migration.
//
// Why a sentinel and not `required` in a form/schema: this is the "no bloquear
// por detalles" side of the rule. The líder is in the field; a MR that never
// gets created because nobody could say whether "1 Durétan" is a cartridge or a
// litre is worse than a MR that says so out loud. `material_request_items` is an
// INTERNAL record with internal fields to mark on (`match_status`,
// `ai_confidence`, `notes`), so the ambiguity has somewhere to live that a
// client never sees. That is exactly the condition the counter-rule requires.
// ---------------------------------------------------------------------------
export const UNIT_UNSPECIFIED = '(sin especificar)';

/**
 * Rebote de `createMaterialRequest` cuando la solicitud no dice qué material
 * es. Sin `inventory_id` y sin (`is_external` + `external_description`) la fila
 * no se puede mostrar en ninguna bandeja ni aprobar con sentido.
 */
export const MATERIAL_SIN_IDENTIFICAR_MSG =
  'La solicitud tiene que identificar el material: elegí un ítem del inventario '
  + '(inventory_id) o marcala como externa (is_external) con su descripción '
  + '(external_description).';

export const AMBIGUOUS_UNIT_NOTE =
  '[AMBIGUO: el líder no especificó la unidad — confirmar con él antes de comprar (#356)]';

/**
 * Reserved machine-tag namespace (#368). Mirrors
 * `stripMachineTags` in mcp-server/src/tools/smartMaterialRequestTools.js.
 * A caller (here: whatever the extraction UI put in `notes`) must never be able
 * to author a tag that downstream readers treat as proof the server wrote it.
 */
// L07 — `SIN-CATALOGO` (lo escribe `enforceCatalogLink`) y `SIN-SALIDA` (lo
// escribe la migración de reparación 20260801090000) entran al namespace
// reservado: un tag que el servidor pone y el llamador NO puede forjar.
// #604 — `UNIDAD-CATALOGO` entra al namespace reservado: la vista lo lee como
// "esta unidad la puso el servidor, no el líder". Si el llamador la pudiera
// escribir, el modelo se auto-firmaría la unidad que inventó.
const MACHINE_TAG_RE = /\[(?:AMBIGUO|INFERIDO|CORREGIDO|REPARTO|SIN-CATALOGO|SIN-SALIDA|UNIDAD-CATALOGO)\b[^\]]*\]/gi;

const stripMachineTags = (text) => {
  if (text == null) return null;
  const cleaned = String(text).replace(MACHINE_TAG_RE, ' ').replace(/\s{2,}/g, ' ').trim();
  return cleaned || null;
};

// ===========================================================================
// L07 — LA SEMÁNTICA DE `match_status`. DEFINICIÓN EXPLÍCITA.
// ===========================================================================
// Gemelo en `mcp-server/src/tools/smartMaterialRequestTools.js`
// (`enforceCatalogLink`). SI TOCÁS UNA, TOCÁ LA OTRA.
//
// LA REGLA:
//     match_status ∈ ('matched','fuzzy')  ⇒  inventory_id IS NOT NULL
//     fulfillment_type = 'from_stock'     ⇒  inventory_id IS NOT NULL
//
// `matched` y `fuzzy` son AFIRMACIONES SOBRE UNA FILA DEL CATÁLOGO: dicen "esta
// línea apunta a `inventory.<inventory_id>`". Sin `inventory_id` no apuntan a
// nada, y ningún consumidor puede actuar sobre ellas: `planMaterialSplit` las
// rutea 100% a compra (materialSplit.js:101) y `withdrawFromStock` no tiene de
// dónde retirar. `from_stock` es peor todavía: afirma que el material sale de
// bodega, de una fila de bodega que no existe.
//
// MEDIDO EN PRODUCCIÓN (2026-07-30, PostgREST con Prefer:count=exact):
//     match_status='matched' ................................. 50
//     ...de esas, con inventory_id NOT NULL ................... 0
//     material_request_items con inventory_id NOT NULL ........ 0 de 149
//     fulfillment_type='from_stock' .......................... 42
//     status='consumed' con stock_movement_id NULL ........... 15
// Las 50 son mentira, las 42 son mentira, y las 15 son la consecuencia visible.
//
// LA CAUSA, en este mismo archivo: `SmartMaterialRequest.js` (la UI web) pone
// `matched_inventory = best` — el OBJETO — y nunca un `inventory_id` suelto.
// `createSmartRequest` leía `item.inventory_id || null` y lo perdía. El carril
// MCP ya resolvía `item.inventory_id ?? item.matched_inventory?.id`: una
// divergencia de carril más (#353, #454), y la que dejaba al retiro de bodega
// sin nada que retirar.
//
// NO SE BLOQUEA AL LÍDER (#356/#357 paso 4): se DEGRADA a
// 'unmatched'/'needs_purchase' y el motivo viaja con la fila. El bug es el
// silencio, no la degradación. `material_request_items` es un registro INTERNO
// con `notes` donde marcar, que es justo la condición que el contra-ejemplo de
// la factura del cliente exige.
//
// PARA L11: un CHECK que codifique esto va a FALLAR contra prod tal como está.
// Hay que degradar primero las 50 filas (migración
// `20260801090000_l07_backfill_match_status_sin_catalogo.sql`, escrita y SIN
// APLICAR) y recién después agregar la restricción.
// ===========================================================================

/** Estados de match que AFIRMAN un vínculo con una fila de `inventory`. */
export const CATALOG_LINKED_MATCH_STATUSES = ['matched', 'fuzzy'];

export const SIN_CATALOGO_NOTE =
  '[SIN-CATALOGO: se declaró un match de inventario pero no llegó ningún ítem del ' +
  'catálogo al que apuntar — se ruteó a compra (L07)]';

/**
 * Hace cumplir la regla de arriba en el momento de escribir.
 *
 * @param {string|null} inventoryId
 * @param {string} [claimedMatch]
 * @param {string} [claimedFulfillment]
 * @returns {{match_status:string, fulfillment_type:string, downgraded:boolean}}
 */
export const enforceCatalogLink = (inventoryId, claimedMatch, claimedFulfillment) => {
  const match = claimedMatch || (inventoryId ? 'matched' : 'unmatched');
  const fulfillment = claimedFulfillment || (inventoryId ? 'from_stock' : 'needs_purchase');

  if (inventoryId) return { match_status: match, fulfillment_type: fulfillment, downgraded: false };

  const claimsCatalog =
    CATALOG_LINKED_MATCH_STATUSES.includes(match) || fulfillment === 'from_stock';
  if (!claimsCatalog) {
    // 'unmatched' y 'user_confirmed_new' (el botón "es un producto nuevo") son
    // coherentes sin id: no afirman nada del catálogo. No se tocan ni se marcan.
    return { match_status: match, fulfillment_type: fulfillment, downgraded: false };
  }

  return { match_status: 'unmatched', fulfillment_type: 'needs_purchase', downgraded: true };
};

/**
 * Get material requests for a specific user with optional filters
 * @param {string} userId - User ID (technician)
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Request status
 * @param {string} filters.projectId - Filter by project
 * @param {string} filters.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} filters.dateTo - End date (YYYY-MM-DD)
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
/*
 * #619 — DOS ROTURAS ARREGLADAS EN LOS `select` DE ESTE ARCHIVO.
 *
 * 1. `task:tasks(...)` iba SIN nombrar la FK, en los CUATRO `select` de material
 *    requests de este servicio. `material_requests` y `tasks` están unidas por
 *    DOS claves foráneas — `material_requests_task_id_fkey` y
 *    `tasks_material_request_id_fkey` (la compuerta de #606) — así que PostgREST
 *    devuelve **PGRST201** y la consulta ENTERA falla. Medido por conducta
 *    contra el stack local: sin FK → PGRST201 "more than one relationship was
 *    found"; con FK → 1 fila.
 *
 *    Es el MISMO mecanismo que mató `approve_quotation` y `get_quotation` del
 *    carril stdio en #609, un carril más allá. Apareció barriendo los tres
 *    carriles con el guard de #619, no leyendo este archivo.
 *
 * 2. `material_name` NO existe en `material_request_items`: la columna es
 *    `item_description`, y PostgREST contestaba 42703 — o sea que esta consulta
 *    seguía muerta después de arreglar el embed. Se deja como ALIAS porque la
 *    pantalla del técnico (`pages/technician/materials/MaterialRequestCard.js`)
 *    lee `item.material_name`; renombrar acá y no allá cambia un error de base
 *    por una tarjeta que dice "Material desconocido".
 *
 * Los comentarios van ACÁ AFUERA y no adentro del template literal: lo de
 * adentro es el parámetro `select` que viaja a PostgREST, no código.
 */
export const getMaterialRequests = async (userId, filters = {}) => {
  try {
    let query = supabase
      .from('material_requests')
      .select(`
        *,
        project:projects(id, project_number, title),
        task:tasks!material_requests_task_id_fkey(id, title, task_number),
        inventory_item:inventory(id, name, sku, unit, current_stock),
        requested_by_profile:profiles!requested_by(id, full_name),
        reviewed_by_profile:profiles!reviewed_by(id, full_name),
        items:material_request_items(id, material_name:item_description, quantity_requested, unit)
      `, { count: 'exact' })
      .eq('requested_by', userId);

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    // Gracefully handle missing table (404) — return empty instead of spamming console
    if (isTableNotFoundError(error)) {
      return { data: [], count: 0, error: null };
    }
    console.error('[MATERIAL_REQUEST] Get requests error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get material requests by project
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Request status
 * @returns {Promise<{data, error}>}
 */
export const getMaterialRequestsByProject = async (projectId, filters = {}) => {
  try {
    let query = supabase
      .from('material_requests')
      .select(`
        *,
        task:tasks!material_requests_task_id_fkey(id, title, task_number),
        inventory_item:inventory(id, name, sku, unit, current_stock),
        requested_by_profile:profiles!requested_by(id, full_name),
        reviewed_by_profile:profiles!reviewed_by(id, full_name)
      `)
      .eq('project_id', projectId);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: [], error: null };
    }
    console.error('[MATERIAL_REQUEST] Get by project error:', error);
    return { data: null, error };
  }
};

/**
 * Create a new material request
 * @param {Object} requestData - Request data
 * @param {string} requestData.project_id - Project ID
 * @param {string} requestData.task_id - Task ID (optional)
 * @param {string} requestData.inventory_id - Inventory item ID (for internal)
 * @param {boolean} requestData.is_external - Whether this is an external purchase
 * @param {string} requestData.external_supplier - External supplier name (if external)
 * @param {string} requestData.external_description - External item description (if external)
 * @param {number} requestData.quantity_requested - Quantity requested
 * @param {string} requestData.unit - Unit of measure
 * @param {string} requestData.notes - Additional notes
 * @param {string} requestData.photo_url - Photo URL (optional)
 * @returns {Promise<{data, error}>}
 */
export const createMaterialRequest = async (requestData) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!requestData.project_id) throw new Error('Se requiere el proyecto');
    if (!requestData.quantity_requested || requestData.quantity_requested <= 0) {
      throw new Error('Se requiere una cantidad valida');
    }
    if (!requestData.unit) throw new Error('Se requiere la unidad');

    // El material se identifica o no se crea. Medido en prod (2026-09-10): tres
    // solicitudes del 13/08 con `is_external: false`, `inventory_id: null` y sin
    // descripción — el "material" sólo en `unit` ("varillas", "ladrillos").
    // Ningún carril podía mostrarlas ni aprobarlas con sentido, y esta función
    // las aceptaba porque validaba proyecto y cantidad y nada más. Gemelo MCP:
    // `createMaterialRequest` en mcp-server/src/tools/materialRequestTools.js.
    const externalDescription = typeof requestData.external_description === 'string'
      ? requestData.external_description.trim()
      : '';
    if (!requestData.inventory_id && !(requestData.is_external && externalDescription)) {
      throw new Error(MATERIAL_SIN_IDENTIFICAR_MSG);
    }

    const insertData = {
      project_id: requestData.project_id,
      task_id: requestData.task_id || null,
      requested_by: user.id,
      quantity_requested: requestData.quantity_requested,
      unit: requestData.unit,
      status: MATERIAL_REQUEST_STATUS.PENDING,
      notes: requestData.notes || null,
      photo_url: requestData.photo_url || null,
      is_external: requestData.is_external || false,
    };

    // Internal vs external material
    if (requestData.is_external) {
      insertData.external_supplier = requestData.external_supplier || null;
      insertData.external_description = requestData.external_description || null;
    } else {
      insertData.inventory_id = requestData.inventory_id || null;
      insertData.batch_id = requestData.batch_id || null;
    }

    const { data, error } = await supabase
      .from('material_requests')
      .insert(insertData)
      .select(`
        *,
        project:projects(id, project_number, title),
        task:tasks!material_requests_task_id_fkey(id, title, task_number),
        inventory_item:inventory(id, name, sku, unit, current_stock),
        requested_by_profile:profiles!requested_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: null, error: new Error('La tabla material_requests aún no existe') };
    }
    console.error('[MATERIAL_REQUEST] Create request error:', error);
    return { data: null, error };
  }
};

/**
 * Cancel a pending material request
 * @param {string} requestId - Request ID
 * @returns {Promise<{data, error}>}
 */
export const cancelMaterialRequest = async (requestId) => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Verify request exists and belongs to user
    const { data: existingRequest, error: fetchError } = await supabase
      .from('material_requests')
      .select('id, status, requested_by')
      .eq('id', requestId)
      .single();

    if (fetchError) throw new Error('Solicitud no encontrada');

    if (existingRequest.requested_by !== user.id) {
      throw new Error('No tiene permiso para cancelar esta solicitud');
    }

    if (existingRequest.status !== MATERIAL_REQUEST_STATUS.PENDING) {
      throw new Error('Solo se pueden cancelar solicitudes pendientes');
    }

    const { data, error } = await supabase
      .from('material_requests')
      .update({
        status: MATERIAL_REQUEST_STATUS.CANCELLED,
      })
      .eq('id', requestId)
      .select(`
        *,
        inventory_item:inventory(id, name, sku)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: null, error: new Error('La tabla material_requests aún no existe') };
    }
    console.error('[MATERIAL_REQUEST] Cancel request error:', error);
    return { data: null, error };
  }
};

/**
 * Get available batches for an inventory item (for preview)
 * @param {string} inventoryId - Inventory item ID
 * @returns {Promise<{data, totalAvailable, error}>}
 */
export const getAvailableBatches = async (inventoryId) => {
  try {
    // #522 — esto era una consulta directa a `inventory_batches` y tenía DOS
    // defectos que se tapaban entre sí.
    //
    // 1. Pedía `batch_number`, una columna que NO EXISTE, y embebía
    //    `supplier:suppliers(...)`, para lo que no hay FK (la tabla tiene
    //    `purchase_id`, no `supplier_id`). PostgREST devolvía 400, y el catch
    //    de abajo lo clasificaba como "la tabla no existe" y devolvía `[]`. O
    //    sea que el selector decía "no hay lotes disponibles" SIEMPRE, para
    //    todos, y parecía un dato en vez de un error. Hoy no se nota porque
    //    `inventory_batches` está vacía en prod; se notaría con la primera
    //    compra con lote, que es justo cuando nadie estaría mirando acá.
    //
    // 2. Traía `unit_cost` —lo que ProMedic PAGÓ— al browser de cualquiera con
    //    `batches_staff_read`, que incluye al técnico. Esconderlo al renderizar
    //    no alcanza: el dato ya viajó y está en la pestaña de red.
    //
    // La RPC resuelve las dos: consulta las columnas reales y decide el costo
    // del lado del servidor (NULL para quien no es admin/supervisor). No se
    // puede hacer con RLS —filtra filas, no columnas— ni con un REVOKE de
    // columna, porque en Supabase todos los usuarios comparten el rol
    // `authenticated` y revocarla se la quitaría también al admin.
    const { data: batches, error } = await supabase
      .rpc('get_available_batches', { p_inventory_id: inventoryId });

    if (error) {
      // Table doesn't exist yet — return empty silently
      if (isTableNotFoundError(error)) {
        return { data: [], totalAvailable: 0, error: null };
      }
      throw error;
    }

    const totalAvailable = batches?.reduce((sum, b) => sum + b.quantity_available, 0) || 0;

    return { data: batches, totalAvailable, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: [], totalAvailable: 0, error: null };
    }
    console.error('[MATERIAL_REQUEST] Get available batches error:', error);
    return { data: null, totalAvailable: 0, error };
  }
};

// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * #607 — El rol del que está aprobando, o `null` si no se pudo determinar.
 *
 * `null` NO significa "no autorizado": significa "no sé". El que autoriza es
 * `trg_enforce_request_approval` en la base. Ver el comentario en
 * `approveSmartRequest`.
 *
 * @returns {Promise<string|null>}
 */
const readActorRole = async () => {
  try {
    const auth = await supabase.auth.getUser();
    const actorId = auth?.data?.user?.id;
    if (!actorId) return null;
    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', actorId)
      .maybeSingle();
    return data?.role ?? null;
  } catch (error) {
    return null;
  }
};

/**
 * #607 — Cerrar la tarea de aprobación (#372) de una solicitud ya APROBADA.
 *
 * UN solo lugar en este carril, llamado por los dos caminos que aprueban desde
 * la web (`approveSmartRequest` y `approveMaterialRequest`). Gemelo del bloque
 * equivalente de `approveSmartMaterialRequest` en
 * `mcp-server/src/tools/smartMaterialRequestTools.js`: mismo filtro de estados
 * abiertos, mismo `completed_at`.
 *
 * Por qué esto NO es un trigger, a diferencia de #605 y #606: sólo el JS sabe
 * si la aprobación fue LIMPIA. Con warnings la solicitud no pasa a 'approved' y
 * la compuerta debe seguir abierta; un trigger sobre `status` no puede
 * distinguirlo, y además dejaría a `approval_task_closed` del carril MCP
 * reportando `false` sobre una tarea que sí se cerró (lo dice explícito la
 * migración 20260808160000).
 *
 * Llamar SÓLO después de que la solicitud ya quedó 'approved':
 * `guard_approval_task_completion` rechaza el 'completed' en cualquier otro
 * estado, y esa excepción abortaría la transacción.
 *
 * @param {string} requestId
 * @returns {Promise<{closed: boolean, error: Error|null}>}
 */
const closeApprovalTask = async (requestId) => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('material_request_id', requestId)
      .in('status', ['pending', 'in_progress', 'blocked', 'review'])
      .select('id');
    if (error) throw error;
    return { closed: (data?.length ?? 0) > 0, error: null };
  } catch (error) {
    console.error('[MATERIAL_REQUEST] Close approval task error:', error);
    return { closed: false, error };
  }
};

/**
 * Get all pending material requests (admin view)
 * @param {Object} filters - Filter options
 * @param {string} filters.projectId - Filter by project
 * @returns {Promise<{data, error}>}
 */
export const getPendingRequests = async (filters = {}) => {
  try {
    let query = supabase
      .from('material_requests')
      .select(`
        *,
        project:projects(id, project_number, title, client:clients!projects_client_id_fkey(company_name, primary_contact_name)),
        task:tasks!material_requests_task_id_fkey(id, title, task_number),
        inventory_item:inventory(id, name, sku, unit, current_stock, min_stock_level),
        requested_by_profile:profiles!requested_by(id, full_name, phone)
      `)
      .eq('status', MATERIAL_REQUEST_STATUS.PENDING);

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }

    query = query.order('created_at', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: [], error: null };
    }
    console.error('[MATERIAL_REQUEST] Get pending requests error:', error);
    return { data: null, error };
  }
};

/**
 * Approve a material request
 * @param {string} requestId - Request ID
 * @param {string} reviewerId - Reviewer user ID
 * @param {string} notes - Approval notes (optional, stored in notes field)
 * @returns {Promise<{data, error}>}
 */
export const approveMaterialRequest = async (requestId, reviewerId, notes = '') => {
  try {
    const { data: request, error: fetchError } = await supabase
      .from('material_requests')
      // #514: `request_number` es la referencia LEGIBLE (MR-2026-0039) que va a
      // `stock_movements.notes`. Antes se interpolaba el uuid de la solicitud.
      .select('id, request_number, status, inventory_id, quantity_requested, unit, project_id, task_id, is_external, external_description')
      .eq('id', requestId)
      .single();

    if (fetchError) throw new Error('Solicitud no encontrada');

    if (request.status !== MATERIAL_REQUEST_STATUS.PENDING) {
      throw new Error('Solo se pueden aprobar solicitudes pendientes');
    }

    const reviewedAt = new Date().toISOString();
    let finalStatus = MATERIAL_REQUEST_STATUS.APPROVED;
    let stockMovementId = null;
    let consumeWarning = null;

    // For internal inventory requests: withdraw the stock.
    // External or requests without an inventory item skip consumption.
    //
    // L07 — pasa por `withdrawFromStock`, igual que la aprobación multi-ítem.
    // Antes llamaba derecho a `consume_inventory_fifo`, que mide contra
    // `inventory_batches` (vacía en prod, y los 61 ítems son
    // `track_batches=false`) y por lo tanto nunca retiraba nada.
    if (!request.is_external && request.inventory_id) {
      const exit = await withdrawFromStock({
        inventory_id: request.inventory_id,
        quantity: request.quantity_requested,
        project_id: request.project_id || null,
        task_id: request.task_id || null,
        reason: 'material_request',
        // #514: referencia LEGIBLE, no el uuid. La relación ya viaja por FK:
        // `material_requests.stock_movement_id` se escribe unas líneas abajo.
        // El carril MCP escribía otra cosa (`MR <uuid>`) — ahora los dos
        // llaman al mismo helper gemelo y no pueden volver a divergir.
        notes: materialConsumptionNote({ request, userNote: notes }),
        created_by: reviewerId,
      });

      if (exit.success) {
        finalStatus = MATERIAL_REQUEST_STATUS.CONSUMED;
        stockMovementId = exit.movement_id;
      } else {
        // El status NO pasa a 'consumed'. Es exactamente la mentira que este
        // lote vino a matar: 15 filas de prod dicen 'consumed' con
        // `stock_movement_id` NULL.
        consumeWarning = `Error al consumir stock: ${exit.message}`;
      }
    }

    // Lo que esta aprobación NO hizo, dicho con nombre. Medido en prod
    // (MR-2026-0022, 2026-09-10): una solicitud EXTERNA de un ítem quedaba
    // 'approved' sin consumir stock —no hay inventory_id— y sin orden de compra
    // —eso sólo lo hace `approveSmartRequest`—, y la bandeja decía "aprobada y
    // stock consumido". Un `[]` acá significa "no hubo nada que callar", no
    // "esta función todavía no aprendió a avisar". Gemelo MCP:
    // `approveMaterialRequest` en mcp-server/src/tools/materialRequestTools.js.
    const notices = [];
    if (finalStatus === MATERIAL_REQUEST_STATUS.APPROVED && !consumeWarning) {
      const ref = request.request_number || requestId;
      if (request.is_external) {
        notices.push(
          `${ref} es material externo (${request.external_description || 'sin descripción'}): `
          + 'no se consumió stock ni se generó orden de compra. Hay que comprarlo aparte.'
        );
      } else if (!request.inventory_id) {
        notices.push(
          `${ref} no identifica ningún material (sin ítem del catálogo ni descripción externa): `
          + 'quedó aprobada sin consumir stock ni generar compra.'
        );
      }
    }

    const updateData = {
      status: finalStatus,
      reviewed_by: reviewerId,
      reviewed_at: reviewedAt,
      stock_movement_id: stockMovementId,
    };

    if (notes || consumeWarning) {
      updateData.notes = [notes, consumeWarning].filter(Boolean).join(' | ');
    }

    const { data, error } = await supabase
      .from('material_requests')
      .update(updateData)
      .eq('id', requestId)
      .select(`
        *,
        project:projects(id, project_number, title),
        inventory_item:inventory(id, name, sku, current_stock),
        requested_by_profile:profiles!requested_by(id, full_name),
        reviewed_by_profile:profiles!reviewed_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    // #607 — si esta solicitud tenía compuerta (#372), se cierra acá.
    //
    // Hoy el camino de UN ítem no crea tarea de aprobación en ningún carril, y
    // el trigger de #606 tampoco se la crea (discrimina por
    // `source_input_type <> 'manual_legacy'`), así que este UPDATE normalmente
    // no toca ninguna fila. Está igual porque `MaterialRequestsInbox` rutea por
    // CANTIDAD DE ÍTEMS, no por tipo: una solicitud smart cuyo INSERT de ítems
    // falló a mitad de camino cae acá teniendo compuerta, y sin esto quedaría
    // aprobada con la tarea abierta para siempre.
    //
    // Sólo en 'approved': con 'consumed' el guard rechaza el 'completed' y la
    // excepción abortaría la aprobación entera.
    if (finalStatus === MATERIAL_REQUEST_STATUS.APPROVED) {
      const closed = await closeApprovalTask(requestId);
      if (closed.error) {
        consumeWarning = [
          consumeWarning,
          `La solicitud quedó aprobada pero no se pudo cerrar su tarea de aprobación (${closed.error.message}).`,
        ].filter(Boolean).join(' | ');
      }
    }

    // `warning` (singular) se conserva para quien ya lo leía; `warnings`
    // (plural) es lo que las bandejas leen — `MaterialRequestsInbox` leía
    // `data.warnings` y esta función devolvía `warning`, así que la advertencia
    // se perdía por el nombre del campo.
    return {
      data,
      warning: consumeWarning,
      warnings: consumeWarning ? [consumeWarning] : [],
      notices,
      error: null,
    };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: null, error: new Error('La tabla material_requests aún no existe') };
    }
    console.error('[MATERIAL_REQUEST] Approve request error:', error);
    return { data: null, error };
  }
};

/**
 * Reject a material request
 * @param {string} requestId - Request ID
 * @param {string} reviewerId - Reviewer user ID
 * @param {string} reason - Rejection reason
 * @returns {Promise<{data, error}>}
 */
export const rejectMaterialRequest = async (requestId, reviewerId, reason) => {
  try {
    if (!reason || reason.trim() === '') {
      throw new Error('Se requiere un motivo de rechazo');
    }

    // Verify request exists and is pending
    const { data: existingRequest, error: fetchError } = await supabase
      .from('material_requests')
      .select('id, status')
      .eq('id', requestId)
      .single();

    if (fetchError) throw new Error('Solicitud no encontrada');

    if (existingRequest.status !== MATERIAL_REQUEST_STATUS.PENDING) {
      throw new Error('Solo se pueden rechazar solicitudes pendientes');
    }

    const { data, error } = await supabase
      .from('material_requests')
      .update({
        status: MATERIAL_REQUEST_STATUS.REJECTED,
        reviewed_by: reviewerId,
        reviewed_at: new Date().toISOString(),
        rejection_reason: reason,
      })
      .eq('id', requestId)
      .select(`
        *,
        project:projects(id, project_number, title),
        inventory_item:inventory(id, name, sku),
        requested_by_profile:profiles!requested_by(id, full_name),
        reviewed_by_profile:profiles!reviewed_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: null, error: new Error('La tabla material_requests aún no existe') };
    }
    console.error('[MATERIAL_REQUEST] Reject request error:', error);
    return { data: null, error };
  }
};

/**
 * Get material request statistics
 * @param {Object} filters - Filter options
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @returns {Promise<{data, error}>}
 */
export const getMaterialRequestStats = async (filters = {}) => {
  try {
    let query = supabase
      .from('material_requests')
      .select('status, quantity_requested');

    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data.length,
      byStatus: {
        pending: data.filter(r => r.status === MATERIAL_REQUEST_STATUS.PENDING).length,
        approved: data.filter(r => r.status === MATERIAL_REQUEST_STATUS.APPROVED).length,
        rejected: data.filter(r => r.status === MATERIAL_REQUEST_STATUS.REJECTED).length,
        consumed: data.filter(r => r.status === MATERIAL_REQUEST_STATUS.CONSUMED).length,
        cancelled: data.filter(r => r.status === MATERIAL_REQUEST_STATUS.CANCELLED).length,
      },
    };

    return { data: stats, error: null };
  } catch (error) {
    if (isTableNotFoundError(error)) {
      return { data: { total: 0, byStatus: { pending: 0, approved: 0, rejected: 0, consumed: 0, cancelled: 0 } }, error: null };
    }
    console.error('[MATERIAL_REQUEST] Get stats error:', error);
    return { data: null, error };
  }
};

// ============================================
// SMART REQUEST FUNCTIONS (multi-item, AI-assisted)
// ============================================

/**
 * Create a smart material request (parent + N child items)
 * @param {Object} payload
 * @param {string} payload.project_id
 * @param {string} [payload.task_id]
 * @param {string} payload.source_input_type - 'text' | 'audio' | 'photo'
 * @param {string} [payload.source_input_text]
 * @param {Array}  payload.items - reviewed items from the AI extraction UI
 */
export const createSmartRequest = async (payload) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (!payload.project_id) throw new Error('Se requiere el proyecto');
    if (!payload.items?.length) throw new Error('Se requiere al menos un ítem');

    // Insert parent (source_input_type / source_input_text come from the new columns in 052)
    const { data: parent, error: parentError } = await supabase
      .from('material_requests')
      .insert({
        project_id: payload.project_id,
        task_id: payload.task_id || null,
        requested_by: user.id,
        source_input_type: payload.source_input_type,
        source_input_text: payload.source_input_text || null,
        source_attachment_url: payload.source_attachment_url || null,
        review_status: 'submitted',
        status: MATERIAL_REQUEST_STATUS.PENDING,
        notes: payload.notes || null,
        // #356 hallazgo 2 — these are NOT "left null". They cannot be:
        // `quantity_requested numeric NOT NULL CHECK (quantity_requested > 0)`
        // and `unit text NOT NULL` (verified by SELECT against prod
        // information_schema / pg_constraint). The real content of a multi-item
        // request lives in `material_request_items`; these two header columns are
        // leftovers of the 1-item model and mean nothing here.
        //
        // Nulling them requires a migration (drop NOT NULL on both, drop the
        // CHECK). Until that lands, the mitigation is that NO reader may trust
        // them: both UI surfaces already gate the header display behind
        // `inventory_id` being set (MaterialRequestsInbox.js:183,
        // SupervisorDashboard.js:400) and `approve_material_request` refuses
        // smart requests outright (materialRequestTools.js:267). Keep the same
        // values as the MCP lane (smartMaterialRequestTools.js) so the two lanes
        // stay byte-identical and one migration fixes both.
        quantity_requested: 1,
        unit: 'varios',
        is_external: false,
      })
      // #603 — paridad con el carril MCP: el número LEGIBLE se pide acá. La
      // pantalla hoy lo re-consulta después (getRequestWithItems trae
      // `request_number` aparte porque la vista no lo expone), así que no había
      // síntoma visible; pero dejar los dos carriles devolviendo cosas
      // distintas es exactamente cómo se separan.
      .select('id, request_number')
      .single();

    if (parentError) throw parentError;

    // Insert child items
    //
    // #356 — an ABSENT unit means "nobody said". It does not mean "unidad".
    // This lane used to write `item.unit || 'unidad'`, which is the bug the
    // issue reported: "1 Durétan Ultra blanco" became `qty 1 / unit 'unidad'`,
    // a plausible-looking value that travels intact to the purchase order.
    // #604 — Y TAMPOCO ALCANZA CON ACTUAR SÓLO SOBRE LA AUSENCIA.
    //
    // Este comentario decía "a supplied value is indistinguishable from a true
    // one (#348)". Dejó de ser cierto con #369: el server tiene el dictado
    // original (`source_input_text`) y puede leer si la palabra está ahí.
    //
    // Y este carril NO está exento por tener pantalla de revisión: la unidad se
    // renderiza en un `InputGroup.Text` de sólo lectura
    // (`src/pages/technician/materials/SmartMaterialRequest.js`), o sea que el
    // técnico la VE pero no la puede corregir. Las unidades acá salen de la
    // MISMA extracción por modelo (`extract_materials_from_*` vía MCP), así que
    // es el mismo bug con un testigo que no puede intervenir. Gemelo de la
    // lógica: `mcp-server/src/tools/smartMaterialRequestTools.js`.
    const ambiguousItems = [];
    const uncatalogedItems = [];
    const catalogUnitItems = [];
    const unverifiableUnitItems = [];

    // #604 — la unidad del catálogo, leída UNA vez. Sólo se usa donde el modelo
    // inventó: una unidad que el líder SÍ dijo nunca se pisa (si dijo "20
    // metros" de un cable que el catálogo cuenta en "rollo", escribir 20 rollos
    // sería mucho peor que la ambigüedad que estamos arreglando).
    // Se consulta SÓLO por los ítems cuya unidad el modelo inventó — igual que
    // el carril MCP. Una unidad dictada no necesita catálogo, y no leer de más
    // mantiene los dos carriles haciendo las mismas llamadas.
    const catalogIds = [...new Set(
      payload.items
        .filter(i => classifyUnitProvenance(i.unit, payload.source_input_text) === UNIT_SOURCE.INVENTED)
        .map(i => i.inventory_id || i.matched_inventory?.id)
        .filter(Boolean)
    )];
    const catalogUnitById = new Map();
    if (catalogIds.length > 0) {
      const { data: catalogRows } = await supabase
        .from('inventory')
        .select('id, unit')
        .in('id', catalogIds);
      for (const r of catalogRows || []) {
        if (typeof r.unit === 'string' && r.unit.trim() !== '') catalogUnitById.set(r.id, r.unit.trim());
      }
    }

    const itemRows = payload.items.map((item, idx) => {
      const description = item.name || item.item_description || null;
      const suppliedUnit = typeof item.unit === 'string' ? item.unit.trim() : '';
      const unitSource = classifyUnitProvenance(suppliedUnit, payload.source_input_text);
      const qty = parseFloat(item.qty || item.quantity_requested) || 1;

      // L07 — LA LÍNEA QUE FALTABA. `SmartMaterialRequest.js` guarda el match
      // como `matched_inventory` (el objeto entero) y NUNCA como un
      // `inventory_id` suelto, así que este lado lo perdía y las 149 filas de
      // prod quedaron con `inventory_id` NULL — sin catálogo no hay retiro
      // posible, por bien que funcione el resto del camino. El carril MCP ya
      // resolvía las dos formas; ésta es la mitad web de esa paridad.
      const inventoryId = item.inventory_id || item.matched_inventory?.id || null;

      const claim = enforceCatalogLink(inventoryId, item.match_status, item.fulfillment_type);
      if (claim.downgraded) {
        uncatalogedItems.push({
          item: idx + 1,
          description,
          claimed_match_status: item.match_status || null,
          claimed_fulfillment_type: item.fulfillment_type || null,
          reason: 'match_claimed_without_inventory_id',
        });
      }

      // #604 — la unidad del catálogo entra SÓLO cuando el modelo inventó.
      const catalogUnit = unitSource === UNIT_SOURCE.INVENTED
        ? (catalogUnitById.get(inventoryId) || null)
        : null;
      const unitIsAbsent = unitSource === UNIT_SOURCE.ABSENT;
      const unitInventedUnresolvable = unitSource === UNIT_SOURCE.INVENTED && !catalogUnit;
      const unitIsUnknown = unitIsAbsent || unitInventedUnresolvable;

      if (unitIsAbsent) {
        ambiguousItems.push({
          item: idx + 1,
          description,
          quantity_requested: qty,
          raw_text: item.raw_text || null,
          reason: 'unit_not_specified',
        });
      } else if (unitInventedUnresolvable) {
        ambiguousItems.push({
          item: idx + 1,
          description,
          quantity_requested: qty,
          raw_text: item.raw_text || null,
          claimed_unit: suppliedUnit,
          reason: 'unit_not_in_dictation',
        });
      } else if (catalogUnit) {
        catalogUnitItems.push({
          item: idx + 1, description, claimed_unit: suppliedUnit, catalog_unit: catalogUnit,
        });
      } else if (unitSource === UNIT_SOURCE.UNVERIFIABLE) {
        unverifiableUnitItems.push({ item: idx + 1, description, claimed_unit: suppliedUnit });
      }

      return {
        request_id: parent.id,
        inventory_id: inventoryId,
        item_description: inventoryId ? null : description,
        quantity_requested: qty,
        unit: unitIsUnknown ? UNIT_UNSPECIFIED : (catalogUnit || suppliedUnit),
        match_status: claim.match_status,
        fulfillment_type: claim.fulfillment_type,
        // An unknown unit means the item is NOT resolved, whatever confidence
        // the extraction reported. Force 0 so any reader ranking by confidence
        // sees this item as unresolved.
        ai_confidence: unitIsUnknown
          ? 0
          : (item.confidence != null ? parseFloat(item.confidence) : null),
        ai_raw_text: item.raw_text || null,
        notes: [
          unitIsAbsent ? AMBIGUOUS_UNIT_NOTE : null,
          unitInventedUnresolvable ? INVENTED_UNIT_NOTE : null,
          catalogUnit ? unitFromCatalogNote(catalogUnit) : null,
          claim.downgraded ? SIN_CATALOGO_NOTE : null,
          // #368: el tag lo pone el servidor o no lo pone nadie. La nota del
          // llamador se limpia ANTES para que su presencia siga siendo prueba.
          stripMachineTags(item.notes),
        ].filter(Boolean).join(' ') || null,
      };
    });

    const { error: itemsError } = await supabase
      .from('material_request_items')
      .insert(itemRows);

    if (itemsError) {
      // #606 — paridad con el carril MCP (`smartMaterialRequestTools.js`, que ya
      // hacía este rollback): sin él quedaba un encabezado SIN ítems, y ese
      // huérfano es venenoso desde que la compuerta existe. `MaterialRequestsInbox`
      // rutea por cantidad de ítems, así que una solicitud smart con 0 ítems se va
      // por `approveMaterialRequest` — el camino que no estaba pensado para tener
      // tarea de aprobación. Mejor que no nazca.
      await supabase.from('material_requests').delete().eq('id', parent.id);
      throw itemsError;
    }

    // #606 — LA COMPUERTA DEL GASTO, LEÍDA Y REPORTADA.
    //
    // No se crea acá: la crea `trg_create_approval_task_on_smart_request`
    // (migración 20260809120000) en la MISMA transacción del INSERT de arriba.
    // Va en la tabla y no en este archivo porque hay tres creadores de
    // solicitudes en dos carriles más el SQL directo, y porque el JWT del
    // técnico no puede listar admins ni escribir `task_assignments`.
    //
    // Lo que sí corresponde acá es NO CALLARSE: el carril MCP devuelve
    // `approval_task_id` y un warning cuando la compuerta no se pudo crear, y
    // esta pantalla tiene que poder decir lo mismo. La lectura puede volver
    // vacía por RLS y no por ausencia, así que un vacío se reporta como "no
    // verificable desde este rol", nunca como "no existe" (#356: el bug es el
    // silencio, pero una alarma falsa también es ruido).
    const { data: gate } = await supabase
      .from('tasks')
      .select('id, task_number')
      .eq('material_request_id', parent.id)
      .maybeSingle();

    // #369 — el "textual del líder" que no es textual.
    //
    // En ESTE carril el original es lo más confiable que el sistema llega a
    // tener: `payload.source_input_text` es el textarea que el técnico tecleó
    // (SmartMaterialRequest.js lo pasa crudo), no algo que el modelo compuso. Si
    // un `raw_text` no aparece ahí, la extracción lo reescribió — y ese string es
    // el que después se muestra entre comillas como palabras del líder.
    //
    // Se avisa, no se bloquea (#357 paso 4): la solicitud es lo que no se puede
    // perder. Mismo cálculo y mismo mensaje que el carril MCP.
    // Se devuelve ESTRUCTURADO y no como prosa: el mensaje que arma
    // `buildRawTextWarning` está escrito para el AGENTE (nombra
    // `source_input_text`, un campo que un técnico no sabe qué es). Quien
    // muestra esto acá es la pantalla, y redacta para la persona que acaba de
    // dictar. La regla de clasificación —lo que sí tiene que ser idéntico entre
    // carriles— es la misma función en los dos.
    const unquotable = findUnquotableItems(
      payload.items.map((i) => ({
        description: i.name || i.item_description || null,
        raw_text: i.raw_text,
      })),
      payload.source_input_text
    );

    // The ticket exists (the líder must not be blocked), but the ambiguity
    // travels WITH it. Returned so the caller can surface it instead of the
    // UI quietly presenting a fabricated unit as fact.
    return {
      data: {
        id: parent.id,
        // #603 — el número que el usuario va a usar para hablar de su solicitud.
        request_number: parent.request_number ?? null,
        // #606 — la compuerta. `null` NO prueba que no exista (puede ser RLS);
        // por eso viaja también `approval_task_verified`.
        approval_task_id: gate?.id ?? null,
        approval_task_number: gate?.task_number ?? null,
        approval_task_verified: !!gate,
        ambiguous_items_count: ambiguousItems.length,
        ambiguous_items: ambiguousItems,
        unverified_raw_text_count: unquotable.length,
        unverified_raw_text: unquotable,
        // L07 — ítems que decían "matched"/"from_stock" sin ninguna fila de
        // catálogo a la que apuntar. Se guardaron como 'unmatched' y van a
        // compra; la pantalla tiene que poder decirlo en vez de mostrar un
        // "en bodega" que no existe.
        uncataloged_items_count: uncatalogedItems.length,
        uncataloged_items: uncatalogedItems,
        // #604 — la unidad que puso el SERVIDOR desde el catálogo (no el
        // líder), y la que no se pudo verificar por falta de dictado original.
        // La pantalla las tiene que poder distinguir de una unidad dictada.
        catalog_unit_items_count: catalogUnitItems.length,
        catalog_unit_items: catalogUnitItems,
        unverifiable_unit_items_count: unverifiableUnitItems.length,
      },
      error: null,
    };
  } catch (error) {
    console.error('[MATERIAL_REQUEST] Create smart request error:', error);
    return { data: null, error };
  }
};

/**
 * Get a request with its child items (uses the v_material_requests_with_items view)
 */
export const getRequestWithItems = async (requestId) => {
  try {
    const { data, error } = await supabase
      .from('v_material_requests_with_items')
      .select('*')
      .eq('id', requestId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('[MATERIAL_REQUEST] Get request with items error:', error);
    return { data: null, error };
  }
};

/**
 * #384 — lee el stock REAL de los ítems linkeados al catálogo (carril web).
 *
 * Gemelo de `fetchCurrentStock` en
 * `mcp-server/src/tools/smartMaterialRequestTools.js`. `inventory.current_stock`
 * es la única autoridad de cuánto hay (#355): NO se consulta
 * `inventory_batches`, que en prod está vacía (los 61 ítems son
 * `track_batches=false`) y daría 0 disponible para todo.
 *
 * `null` = "no sé", no "no hay". El planificador lo trata como desconocido y
 * cae al ruteo viejo en vez de comprar todo.
 *
 * #513 — exportada: el PREVIEW previo a aprobar (`useMaterialSplitPreview`) lee
 * el stock con esta misma función. Si el preview hiciera su propia query, la
 * pantalla y la aprobación podrían leer cosas distintas — que es la clase de
 * deriva que causó #513 en primer lugar.
 */
export const fetchCurrentStock = async (inventoryIds) => {
  if (!Array.isArray(inventoryIds) || inventoryIds.length === 0) {
    return { stockById: new Map(), itemsById: new Map(), error: null };
  }
  const { data, error } = await supabase
    .from('inventory')
    // L07 — ahora también `track_batches`: es el dato que decide si el retiro va
    // por FIFO o por un movimiento directo, y ya se está leyendo esta fila.
    // Releerla por ítem sería un N+1 y, peor, dos lecturas que pueden discrepar.
    .select('id, name, current_stock, track_batches')
    .in('id', inventoryIds);

  if (!Array.isArray(data)) return { stockById: null, itemsById: null, error: error || null };

  const stockById = new Map();
  const itemsById = new Map();
  for (const row of data) {
    if (!row?.id) continue;
    itemsById.set(row.id, row);
    const v = parseFloat(row?.current_stock);
    if (Number.isFinite(v)) stockById.set(row.id, Math.max(0, v));
  }
  return { stockById, itemsById, error: error || null };
};

// ===========================================================================
// L07 — LA SALIDA DE BODEGA. Gemelo de `mcp-server/src/services/stockExit.js`.
// ===========================================================================
// No se importan entre sí a propósito: `src/**` es el bundle del browser y
// `mcp-server/**` es un paquete Node aparte con su propio package.json y su
// propio Root Dir en Vercel. Misma duplicación deliberada que `materialSplit`,
// `rawTextFidelity` y `auditRef`. SI TOCÁS UNA, TOCÁ LA OTRA.
//
// EL PROBLEMA (medido en producción el 2026-07-30):
//   stock_movements .... 0 filas en toda la historia del sistema
//   inventory_batches .. 0 filas
//   inventory .......... 61 ítems, los 61 con track_batches=false
//   material_request_items: 149 filas, 0 con stock_movement_id, y 15 con
//                           status='consumed' igual — el registro afirma que
//                           el material salió de bodega y nunca salió.
//
// `consume_inventory_fifo` mide la disponibilidad así:
//     SELECT COALESCE(SUM(quantity_available),0) FROM inventory_batches ...
// o sea contra una tabla VACÍA, sobre ítems que por diseño nunca van a tener
// lotes. Da 0 siempre ⇒ "Insufficient stock. Available: 0, Requested: N" para
// todo. `inventory.current_stock` —la única autoridad de cuánto hay (#355)— no
// se consultaba nunca.
//
// El patrón correcto ya existía en `mcp-server/src/tools/inventoryTools.js`
// desde #287: FIFO sólo si el ítem lleva lotes; si no, se inserta el
// `stock_movements` derecho y el trigger `update_inventory_stock_trigger` hace
// la aritmética de `current_stock`.
//
// INVARIANTES (idénticas en los dos carriles):
//   1. `track_batches !== false` ⇒ FIFO (`!== false`, no `=== true`: un NULL
//      cae del lado conservador).
//   2. `track_batches === false` ⇒ INSERT directo, SIEMPRE con `project_id`
//      (DB CHECK valid_exit_movement). Se valida antes de escribir para no
//      devolver un 23514 crudo.
//   3. `quantity` va con magnitud POSITIVA: el trigger RESTA (#287).
//   4. Nadie escribe `inventory.current_stock` desde la aplicación (#394).
//   5. Falta de stock ⇒ no se escribe nada y el mensaje lleva el `current_stock`
//      REAL, para que el llamador mande a comprar el faltante y sólo el faltante.
//   6. Fila de `inventory` ilegible (RLS / borrada) ⇒ se DELEGA en el FIFO en vez
//      de inventar un camino (misma degradación conservadora de #384; además el
//      FIFO es SECURITY DEFINER y ve filas que este cliente puede no ver).
// ===========================================================================

/** El ítem lleva lotes: se consumió por `consume_inventory_fifo`. */
export const EXIT_PATH_FIFO = 'fifo';
/** El ítem NO lleva lotes: se insertó un `stock_movements` exit directo. */
export const EXIT_PATH_DIRECT = 'direct';

const stockExitFailure = (message, path = null) => ({
  success: false,
  path,
  message,
  movement_id: null,
  movements_created: 0,
  batches_affected: [],
});

/**
 * Retira `quantity` de bodega y devuelve el movimiento que lo prueba.
 *
 * @param {object} params
 * @param {string} params.inventory_id - OBLIGATORIO. Sin catálogo no hay de
 *   dónde retirar; esa cantidad va a la orden de compra, no acá.
 * @param {number} params.quantity - magnitud positiva
 * @param {string} params.project_id - OBLIGATORIO para 'exit'
 * @param {string|null} [params.task_id]
 * @param {string} [params.reason='consumption']
 * @param {string|null} [params.notes]
 * @param {string|null} [params.created_by]
 * @param {object|null} [params.item] - fila de `inventory` ya leída.
 *   `undefined` = la lee este helper; `null` = el llamador buscó y no vino
 *   (invariante 6 ⇒ FIFO, sin releer); objeto = se decide con eso.
 * @returns {Promise<{success:boolean, path:string|null, movement_id:string|null,
 *   movements_created:number, message:string, batches_affected:Array}>}
 */
export const withdrawFromStock = async (params = {}) => {
  const {
    inventory_id,
    quantity,
    project_id,
    task_id = null,
    reason = 'consumption',
    notes = null,
    created_by = null,
  } = params;

  const qty = Number(quantity);

  if (!inventory_id) {
    return stockExitFailure(
      'withdrawFromStock: inventory_id is required — an item with no catalog link ' +
      'has no stock to withdraw from (it must go to the purchase order instead).'
    );
  }
  if (!Number.isFinite(qty) || qty <= 0) {
    return stockExitFailure(`withdrawFromStock: quantity must be a positive number (got ${JSON.stringify(quantity)}).`);
  }
  if (!project_id) {
    return stockExitFailure(
      'withdrawFromStock: project_id is required for exit movements ' +
      '(DB CHECK valid_exit_movement).'
    );
  }

  let item = params.item;
  if (item === undefined) {
    const { data, error } = await supabase
      .from('inventory')
      .select('id, name, track_batches, current_stock')
      .eq('id', inventory_id)
      .maybeSingle();
    item = error ? null : data;
  }

  // ---- Invariantes 1 y 6: con lotes (o sin poder saberlo), manda el FIFO ----
  if (!item || item.track_batches !== false) {
    const { data: fifo, error: fifoErr } = await supabase.rpc('consume_inventory_fifo', {
      p_inventory_id: inventory_id,
      p_quantity: qty,
      p_project_id: project_id,
      p_task_id: task_id,
      p_reason: reason,
      p_notes: notes,
      p_created_by: created_by,
    });

    const row = Array.isArray(fifo) ? fifo[0] : fifo;
    if (fifoErr || !row?.success) {
      return stockExitFailure(
        row?.message || fifoErr?.message || 'consume_inventory_fifo failed',
        EXIT_PATH_FIFO
      );
    }
    return {
      success: true,
      path: EXIT_PATH_FIFO,
      movement_id: row.batches_affected?.[0]?.movement_id ?? null,
      movements_created: row.movements_created ?? (row.batches_affected?.length ?? 0),
      message: row.message || `Consumed ${qty} units via FIFO`,
      batches_affected: row.batches_affected || [],
    };
  }

  // ---- Invariante 5: sin lotes, la autoridad es current_stock --------------
  const rawStock = Number(item.current_stock);
  const available = Number.isFinite(rawStock) ? rawStock : 0;
  if (available < qty) {
    // Mismo formato que `consume_inventory_fifo` a propósito: es el texto que
    // los lectores de `rejection_reason` ya conocen. Lo que cambia es que el
    // número por fin es real.
    return {
      ...stockExitFailure(`Insufficient stock. Available: ${available}, Requested: ${qty}`, EXIT_PATH_DIRECT),
      available_stock: available,
    };
  }

  // Invariantes 3 y 4: magnitud positiva y ni una línea que toque current_stock.
  const { data: movement, error: movementErr } = await supabase
    .from('stock_movements')
    .insert({
      inventory_id,
      movement_type: 'exit',
      quantity: qty,
      project_id,
      task_id,
      reason,
      notes,
      created_by,
      // batch_id / unit_cost_at_move / total_cost quedan NULL a propósito: el
      // ítem no lleva lotes, no hay costo histórico por movimiento. Poner 0
      // diría "salió gratis".
    })
    .select('id')
    .single();

  if (movementErr) {
    return stockExitFailure(
      `withdrawFromStock: failed to record stock movement: ${movementErr.message}`,
      EXIT_PATH_DIRECT
    );
  }

  return {
    success: true,
    path: EXIT_PATH_DIRECT,
    movement_id: movement?.id ?? null,
    movements_created: 1,
    message: `Successfully withdrew ${qty} units of ${item.name || inventory_id} from stock`,
    batches_affected: [],
    available_stock: available,
  };
};

/**
 * Approve a smart (multi-item) request.
 *
 * #384 (brecha O-1) — RETIRO PARCIAL + COMPRA PARCIAL, POR CANTIDAD.
 * Carril WEB del mismo fix que `approveSmartMaterialRequest` en
 * `mcp-server/src/tools/smartMaterialRequestTools.js`. Los dos carriles tienen
 * que decidir IGUAL: si sólo se arreglara el MCP, Jorge — que aprueba desde la
 * web — seguiría comprando los 100 tubos teniendo 60 en bodega (#353, #454).
 *
 * Por ítem:
 *     retirar = min(necesario, current_stock)   → consumo FIFO
 *     comprar = necesario − retirar             → línea en la PO borrador
 *     quantity_approved = necesario             (= retirar + comprar, invariante)
 *
 * Si el FIFO falla, la cantidad NO se pierde: pasa entera a la orden de compra.
 * Es imprescindible porque `consume_inventory_fifo` mide disponibilidad contra
 * `inventory_batches` (vacía en prod) y hoy devuelve "Insufficient stock.
 * Available: 0" para todo — sin el fallback, esas unidades ni se retirarían ni
 * se comprarían.
 */
export const approveSmartRequest = async (requestId, reviewerId) => {
  try {
    // #607 — EL "NO PODÉS" TIENE QUE LLEGAR ANTES DE LA MITAD IRREVERSIBLE.
    //
    // `trg_enforce_request_approval` (20260807160000, VERIFICADO EN PROD con
    // `pg_get_triggerdef`) sólo deja a un admin mover una solicitud a
    // 'approved'. Sin este chequeo previo, un supervisor recorre TODA la
    // función —consumo de stock, orden de compra, nota de bitácora— y recién al
    // final se come el 42501: el gasto ejecutado y la solicitud sin aprobar.
    //
    // Esto es PINTURA, no seguridad: quien manda es el trigger, y por eso FALLA
    // ABIERTO. Sólo frena cuando leyó un rol y ese rol NO es admin; si no puede
    // determinarlo (sin sesión, sin fila de perfil, RLS), sigue de largo y deja
    // decidir a la base — que es la que decide igual. Bloquear acá por no poder
    // leer sería inventar una denegación que la DB no haría.
    const actorRole = await readActorRole();
    if (actorRole && actorRole !== 'admin') {
      return {
        data: null,
        error: new Error(
          'Solo un admin puede aprobar una solicitud de material (#550). ' +
          'No se consumió stock ni se creó ninguna orden de compra.'
        ),
      };
    }

    const { data: req, error: fetchErr } = await supabase
      .from('v_material_requests_with_items')
      .select('*')
      .eq('id', requestId)
      .single();

    if (fetchErr) throw fetchErr;

    // #514: `v_material_requests_with_items` NO expone `request_number`
    // (verificado contra producción el 2026-07-29 leyendo
    // `information_schema.columns` de la vista). Sin este dato la nota del
    // carril web perdería el número y volvería a divergir del carril MCP, que
    // sí lo tiene. Una lectura chica de una sola columna, en una acción de
    // admin que ocurre una vez por aprobación — no en un render.
    // ARREGLO DE FONDO (fuera de alcance acá): agregarle `request_number` a la
    // vista y borrar esta consulta.
    const { data: reqNumberRow } = await supabase
      .from('material_requests')
      .select('request_number')
      .eq('id', requestId)
      .maybeSingle();
    if (reqNumberRow?.request_number) req.request_number = reqNumberRow.request_number;

    const items = Array.isArray(req.items) ? req.items : JSON.parse(req.items || '[]');
    // `warnings` = fallas duras. `notices` = informativo (el reparto, la
    // degradación al modo viejo) que NO debe leerse como que algo falló.
    const warnings = [];
    const notices = [];
    const pending = items.filter(i => i.status === 'pending');

    // 0. #664 — LAS LÍNEAS SIN CANTIDAD NO PASAN DE ACÁ.
    //
    // Gemelo EXACTO del guard de `approveSmartMaterialRequest` (carril MCP).
    // Arreglar un solo carril es el patrón de #353/#454: el issue queda
    // "cerrado" mientras la web —el driver diario de Jorge— sigue aprobando
    // líneas de cantidad cero.
    //
    // Desde #664 una línea puede nacer con `quantity_requested` NULL ("nadie lo
    // dijo") para no bloquear la captura en obra. Aprobar es otra cosa: retira
    // stock y arma una orden de compra. `planMaterialSplit` convierte ese NULL
    // en `needed = 0` y aprueba una línea de cero unidades sin decir nada —
    // exactamente el silencio que #356 vino a matar.
    //
    // VA ANTES DE TODO EFECTO, por lo mismo que en el otro carril: un guard
    // después del retiro deja stock consumido y solicitud sin aprobar (#607).
    const sinCantidad = pending.filter(
      i => i.quantity_requested == null || !Number.isFinite(parseFloat(i.quantity_requested))
    );
    if (sinCantidad.length > 0) {
      const nombres = sinCantidad
        .map(i => `"${i.item_description || i.ai_raw_text || i.id}"`)
        .join(', ');
      return {
        data: null,
        error: new Error(
          `No se puede aprobar: ${sinCantidad.length} línea(s) no tienen cantidad porque nadie la ` +
          `dijo — ${nombres}. Completá la cantidad de cada una antes de aprobar. No se consumió ` +
          'stock ni se creó ninguna orden de compra.'
        ),
      };
    }

    // 1. Disponibilidad real, de una sola lectura
    const inventoryIds = [...new Set(pending.map(i => i.inventory_id).filter(Boolean))];
    const { stockById, itemsById, error: stockErr } = await fetchCurrentStock(inventoryIds);
    if (stockErr) {
      notices.push(
        `No se pudo leer el stock actual (${stockErr.message}): el reparto por cantidad quedó ` +
        'deshabilitado y cada ítem se ruteó con la regla vieja (todo de bodega o todo de compra).'
      );
    }

    // 2. El plan: retirar = min(necesario, disponible), comprar = resto
    const plans = planMaterialSplit(pending, stockById);

    // 3. Pata BODEGA: consumo FIFO de lo disponible
    for (const plan of plans) {
      if (plan.quantity_from_stock <= 0) continue;

      // L07 — ya NO se llama derecho a `consume_inventory_fifo`. El helper
      // decide por `inventory.track_batches`: FIFO si el ítem lleva lotes,
      // movimiento directo si no (los 61 ítems de prod son track_batches=false
      // y `inventory_batches` está vacía, así que el FIFO devolvía
      // "Available: 0" para todo y nada salió nunca de bodega).
      const exit = await withdrawFromStock({
        inventory_id: plan.inventory_id,
        quantity: plan.quantity_from_stock,
        project_id: req.project_id || null,
        task_id: req.task_id || null,
        reason: 'material_request',
        // #514: nada de uuids en texto libre. La relación ya viaja por FK —
        // `material_request_items.stock_movement_id` se escribe más abajo.
        notes: materialConsumptionNote({ request: req, item: plan.item }),
        created_by: reviewerId,
        // La fila ya se leyó arriba. `null` explícito = "la busqué y no vino"
        // ⇒ el helper delega en el FIFO (degradación conservadora de #384).
        item: itemsById ? (itemsById.get(plan.inventory_id) ?? null) : null,
      });

      if (!exit.success) {
        const msg = exit.message || 'Error desconocido';
        // El retiro no ocurrió ⇒ esa cantidad se COMPRA. El total se conserva.
        const rescued = plan.quantity_from_stock;
        plan.quantity_to_purchase = round2(plan.quantity_to_purchase + rescued);
        plan.quantity_from_stock = 0;
        plan.split = false;
        plan.fifo_error = msg;
        warnings.push(
          `Ítem "${plan.item.item_description || plan.inventory_id}": ${msg} — esas ` +
          `${formatQty(rescued)} unidad(es) pasaron a la orden de compra borrador.`
        );
      } else {
        plan.stock_movement_id = exit.movement_id ?? null;
      }
    }

    // 4. Pata COMPRA: el FALTANTE de cada ítem, en una sola PO borrador
    const purchasePlans = plans.filter(p => p.quantity_to_purchase > 0);
    let poId = null;
    if (purchasePlans.length) {
      const { data: po, error: poErr } = await supabase
        .from('purchases')
        .insert({
          // Flow B draft PO. 'pending' was dropped from purchases_status_check
          // by migration 065; inserting it made PO creation fail silently (#268).
          status: 'draft',
          // L10 — el carril web no guardaba el proyecto NI acá NI en la línea,
          // así que el vínculo se perdía sin retorno: sin encabezado tagueado
          // ni siquiera se puede reparar por herencia. El carril MCP sí lo
          // guardaba en el encabezado (createDraftPurchaseFromShortfall).
          project_id: req.project_id || null,
          po_date: new Date().toISOString().slice(0, 10),
          // #514: el vínculo real es `material_request_items.purchase_item_id`,
          // que se re-enlaza más abajo.
          notes: draftPurchaseNote(req),
          created_by: reviewerId,
        })
        .select('id')
        .single();

      if (poErr) {
        warnings.push(`No se pudo crear la orden de compra: ${poErr.message}`);
      } else {
        poId = po.id;
        for (const plan of purchasePlans) {
          const item = plan.item;
          const { data: pi, error: piErr } = await supabase
            .from('purchase_items')
            .insert({
              purchase_id: po.id,
              inventory_id: item.inventory_id || null,
              description: item.inventory_id ? null : (item.item_description || item.ai_raw_text),
              // #384: el faltante, no la cantidad entera.
              quantity_ordered: plan.quantity_to_purchase,
              unit_cost: 0,
              // L10 — la compra nació PARA este proyecto; sin esta columna
              // ningún consumidor de costo la ve (committed_amount, el gasto
              // al facturar, el resumen del proyecto: los tres filtran por
              // `purchase_items.project_id`). La solicitud SIEMPRE tiene
              // proyecto, así que acá no hay caso "compra de bodega".
              project_id: req.project_id || null,
            })
            .select('id')
            .single();

          if (!piErr && pi) plan.purchase_item_id = pi.id;
        }
      }
    }

    // 5. Persistir el resultado por ítem, en UNA sola escritura por fila
    //    (un ítem repartido tiene DOS resultados sobre la misma fila).
    for (const plan of plans) {
      const hasOutcome = plan.stock_movement_id || plan.purchase_item_id || plan.fifo_error;
      if (!hasOutcome) continue;

      const patch = { quantity_approved: plan.needed };
      patch.status = (plan.quantity_to_purchase <= 0 && plan.quantity_from_stock > 0)
        ? 'consumed'
        : 'approved';
      if (plan.stock_movement_id) patch.stock_movement_id = plan.stock_movement_id;
      if (plan.purchase_item_id) patch.purchase_item_id = plan.purchase_item_id;
      if (plan.fifo_error) patch.rejection_reason = plan.fifo_error;
      if (plan.split && plan.purchase_item_id) {
        patch.notes = stampSplitNote(
          plan.item.notes,
          plan.quantity_from_stock,
          plan.quantity_to_purchase,
          plan.item.unit
        );
      }

      await supabase.from('material_request_items').update(patch).eq('id', plan.item.id);
    }

    const splitPlans = plans.filter(p => p.split);
    if (splitPlans.length > 0) {
      notices.push(
        `${splitPlans.length} ítem(s) se repartieron entre bodega y compra: ` +
        splitPlans.map(p => {
          const label = p.item.item_description || p.item.ai_raw_text || p.item.id;
          return `"${label}" → retirar ${formatQty(p.quantity_from_stock)} de bodega y comprar ` +
            `${formatQty(p.quantity_to_purchase)} (de ${formatQty(p.needed)})`;
        }).join('; ') + '.'
      );
    }

    // 5b. Bitácora note (#268): a "necesito comprar X" approval must also
    // land a progress_reports entry so the purchase shows in the project
    // timeline. Non-fatal — a bitácora failure never blocks the approval.
    if (poId) {
      const itemLabels = purchasePlans
        .map(p => p.item.item_description || p.item.ai_raw_text)
        .filter(Boolean);
      const itemsText = itemLabels.length ? itemLabels.join(', ') : 'materiales solicitados';
      const { error: bitacoraErr } = await supabase.from('progress_reports').insert({
        project_id: req.project_id,
        report_date: new Date().toISOString().split('T')[0],
        description: `Solicitud de material aprobada: orden de compra creada para ${itemsText}.`,
        created_by: reviewerId,
      });
      if (bitacoraErr) {
        warnings.push(`No se pudo registrar la nota en bitácora: ${bitacoraErr.message}`);
      }
    }

    // 6. Marcar la solicitud revisada — y APROBADA si la corrida fue limpia.
    //
    // #607 — ACÁ FALTABA `status`. Esta función hacía todo el trabajo pesado
    // (reparto bodega/compra, consumo de stock, orden de compra, bitácora) y
    // después escribía SÓLO `review_status`: la solicitud quedaba en 'pending'
    // con el stock ya consumido, se leía como si nadie la hubiera visto, y —lo
    // caro— su tarea de aprobación no se cerraba nunca, porque tanto el JS como
    // el trigger de #605 dependen de que el ESTADO cambie.
    //
    // La condición es la del carril MCP (`smartMaterialRequestTools.js`), no un
    // `status='approved'` incondicional: con warnings la solicitud queda
    // 'pending' para re-revisión y la tarea DEBE seguir abierta. Cerrarla ahí
    // sería el botón mentiroso de #372.
    const mrPatch = {
      review_status: 'reviewed',
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
    };
    if (warnings.length === 0) mrPatch.status = MATERIAL_REQUEST_STATUS.APPROVED;
    const { error: mrUpdateErr } = await supabase
      .from('material_requests')
      .update(mrPatch)
      .eq('id', requestId);
    if (mrUpdateErr) {
      // El error de esta escritura NO se miraba. Un 42501 de
      // `trg_enforce_request_approval` (el que le llega a quien no es admin)
      // pasaba entero desapercibido y reproducía #607 con otro mecanismo:
      // stock consumido, orden creada, estado intacto y nadie enterado.
      warnings.push(
        `La solicitud NO quedó marcada como aprobada (${mrUpdateErr.message}). ` +
        'El stock y la orden de compra de arriba SÍ se ejecutaron: revisala a mano.'
      );
    }

    // 7. La compuerta se cierra DESPUÉS de que la solicitud quedó aprobada.
    //
    // El orden no es convención: `guard_approval_task_completion`
    // (20260718160000) sólo deja pasar la tarea a 'completed' cuando su
    // solicitud ya está 'approved' de verdad. Y si hubo warnings no se toca
    // nada — todavía hay algo que aprobar.
    //
    // Gemelo de `approveSmartMaterialRequest` (carril MCP): mismo filtro de
    // estados abiertos, mismo degradado a warning. No revierte la aprobación
    // (el gasto ya se autorizó), pero tampoco se calla: una tarea abierta sobre
    // algo ya aprobado es la otra mitad del split-brain de #372.
    let approvalTaskClosed = false;
    if (warnings.length === 0) {
      const closed = await closeApprovalTask(requestId);
      approvalTaskClosed = closed.closed;
      if (closed.error) {
        warnings.push(
          'La solicitud quedó aprobada pero no se pudo cerrar su tarea de aprobación ' +
          `(${closed.error.message}): puede seguir apareciendo como pendiente. Ya está aprobada.`
        );
      }
    }

    return {
      data: {
        requestId,
        warnings,
        notices,
        approval_task_closed: approvalTaskClosed,
        draft_purchase_id: poId,
        split_items_count: splitPlans.length,
        split: plans.map(p => ({
          item_id: p.item.id,
          description: p.item.item_description || p.item.ai_raw_text || null,
          unit: p.item.unit || null,
          quantity_requested: p.needed,
          quantity_approved: p.needed,
          quantity_from_stock: p.quantity_from_stock,
          quantity_to_purchase: p.quantity_to_purchase,
          available_stock: p.available_stock,
          stock_movement_id: p.stock_movement_id || null,
          purchase_item_id: p.purchase_item_id || null,
          was_split: !!p.split,
          stock_unknown: !!p.stock_unknown,
        })),
      },
      error: null,
    };
  } catch (error) {
    console.error('[MATERIAL_REQUEST] Approve smart request error:', error);
    return { data: null, error };
  }
};

const materialRequestService = {
  getMaterialRequests,
  getMaterialRequestsByProject,
  createMaterialRequest,
  cancelMaterialRequest,
  getAvailableBatches,
  getPendingRequests,
  approveMaterialRequest,
  rejectMaterialRequest,
  getMaterialRequestStats,
  createSmartRequest,
  getRequestWithItems,
  approveSmartRequest,
};

export default materialRequestService;
